import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@loader/ui';
import { Loader2 } from 'lucide-react';

import { SchedulePicker } from './SchedulePicker';
import { DEFAULT_SCHEDULE } from './schedulePickerUtils';

interface SqlFetchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading: boolean;
  onSubmit: (data: {
    name: string;
    config: {
      connection_type: 'postgresql';
      connection_string: string;
      query: string;
    };
    schedule?: string;
  }) => void;
}

export function SqlFetchDialog({ open, onOpenChange, loading, onSubmit }: SqlFetchDialogProps) {
  const [name, setName] = useState('');
  const [connectionString, setConnectionString] = useState('');
  const [query, setQuery] = useState('');
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: name || 'SQL Query',
      config: {
        connection_type: 'postgresql',
        connection_string: connectionString,
        query,
      },
      schedule: schedule || undefined,
    });
  };

  const reset = () => {
    setName('');
    setConnectionString('');
    setQuery('');
    setSchedule(DEFAULT_SCHEDULE);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>SQL Query Connector</DialogTitle>
            <DialogDescription>
              Pull data from a PostgreSQL database on a schedule.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="sql-name">Table Name</Label>
              <Input
                id="sql-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Customer Data"
              />
            </div>

            <div className="grid gap-2">
              <Label>Database</Label>
              <Select value="postgresql" disabled>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="postgresql">PostgreSQL</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">MySQL support coming soon.</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="connectionString">Connection String</Label>
              <Input
                id="connectionString"
                type="password"
                value={connectionString}
                onChange={(e) => setConnectionString(e.target.value)}
                placeholder="postgresql://user:pass@host:5432/db"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="query">SQL Query</Label>
              <Textarea
                id="query"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="SELECT * FROM customers WHERE active = true"
                className="font-mono text-xs"
                rows={4}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label>Schedule</Label>
              <SchedulePicker value={schedule} onChange={setSchedule} />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!connectionString || !query || loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create & Sync
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
